import { useMemo, type JSX } from 'react';
import { X } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';

/**
 * Languages the server's offline detector can tell apart (spade-discovery
 * services/geo/language.ts). Offering anything else would promise a filter
 * that can't be applied.
 */
export const DETECTABLE_LANGUAGES = [
  'en', 'es', 'pt', 'fr', 'de', 'nl', 'it', 'id', 'ms', 'fil', 'tr', 'vi', 'pl', 'sv', 'ro', 'sw',
  'ar', 'fa', 'ur', 'hi', 'bn', 'pa', 'gu', 'ta', 'te', 'kn', 'ml', 'si', 'th', 'lo', 'my', 'km',
  'ko', 'ja', 'zh', 'ru', 'uk', 'he', 'el', 'hy', 'ka', 'am',
] as const;

const names = new Intl.DisplayNames(['en'], { type: 'language', fallback: 'code' });
export function languageName(code: string): string {
  try {
    return names.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Chosen languages as removable chips, plus a type-to-find box to add more. */
export function LanguagePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}): JSX.Element {
  const options = useMemo(
    () =>
      DETECTABLE_LANGUAGES.filter((l) => !value.includes(l))
        .map((l) => ({ value: l, label: languageName(l) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [value],
  );
  return (
    <div className="grid gap-1.5">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 py-0.5 pl-2 pr-1 text-sm"
            >
              {languageName(code)}
              <button
                type="button"
                onClick={() => onChange(value.filter((v) => v !== code))}
                aria-label={`Remove ${languageName(code)}`}
                title={`Remove ${languageName(code)}`}
                className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Combobox
        value=""
        onChange={(code) => code && onChange([...value, code])}
        options={options}
        placeholder={value.length ? 'Add another language…' : 'Any language'}
      />
    </div>
  );
}
